import firebase from 'firebase/app'
import 'firebase/messaging'
import { BaseHook, BaseNotification, BaseNotificationClient } from '@/types'
import { createHooks } from '@/utils/hook-util'
import { sleep } from '@/utils/timer-util'

const FIREBASE_OPTIONS: string = process.env.VUE_APP_FIREBASE_OPTIONS
const FIREBASE_SUPPORT_APP = !!((process.env.NODE_ENV === 'production') && FIREBASE_OPTIONS)
const FIREBASE_SUPPORT_MESSAGING = (FIREBASE_SUPPORT_APP && firebase.messaging.isSupported())

const notificationHooks = createHooks<BaseNotification>()
const notificationClientHooks = createHooks<BaseNotificationClient | undefined>()

export default class BaseFirebase {
  private subscribing: boolean
  private unsubscribing: boolean
  private messaging?: firebase.messaging.Messaging
  private lastNotificationPermission?: NotificationPermission

  constructor() {
    if (FIREBASE_SUPPORT_APP) firebase.initializeApp(JSON.parse(FIREBASE_OPTIONS))

    this.subscribing = false
    this.unsubscribing = false

    if (FIREBASE_SUPPORT_MESSAGING) {
      this.messaging = firebase.messaging()

      this.messaging?.onMessage((messagePayload: firebase.messaging.MessagePayload) => {
        const data: any = messagePayload.notification ?? {} //TODO: type

        notificationHooks.execute({
          ...data,
          title: data.body ?? data.title,
          clickAction: data.clickAction ?? data.click_action,
          datetime: data.datetime ?? (data.timestamp ? new Date(data.timestamp).toISOString() : undefined)
        })
      })

      this.lastNotificationPermission = Notification?.permission

      Object.defineProperty(self, 'requestNotificationPermission', {
        get: () => Notification.requestPermission().then(() => this.setEnabledNotification(true))
      })
    }
  }

  async setEnabledNotification(enabledNotification: boolean): Promise<void> {
    if (!FIREBASE_SUPPORT_MESSAGING) return

    if (enabledNotification) {
      switch (Notification.permission) {
        case 'default': {
          notificationHooks.execute({
            title: 'app.notification.requestNotificationPermission',
            clickAction: 'javascript:self.requestNotificationPermission;',
            clickTitle: 'app.button.enable',
            uniqueKey: 'self.requestNotificationPermission',
            temporary: true
          })

          break
        }
        case 'granted': {
          await this.subscribe()

          if (Notification.permission !== this.lastNotificationPermission) {
            notificationHooks.execute({
              title: 'app.notification.grantedNotificationPermission',
              temporary: true,
              autoremove: true
            })
          }

          break
        }
        case 'denied': {
          await this.unsubscribe()

          break
        }
      }
    }
    else await this.unsubscribe()

    this.lastNotificationPermission = Notification.permission
  }

  registerNotificationHook(notificationHook: BaseHook<BaseNotification>): void {
    if (!FIREBASE_SUPPORT_MESSAGING) return

    notificationHooks.register(notificationHook)
  }

  registerNotificationClientHook(notificationClientHook: BaseHook<BaseNotificationClient | undefined>): void {
    if (!FIREBASE_SUPPORT_MESSAGING) return

    notificationClientHooks.register(notificationClientHook)
  }

  private async subscribe(): Promise<void> {
    while (this.unsubscribing) await sleep(500)

    try {
      this.subscribing = true

      const token = await this.messaging?.getToken()

      if (token) notificationClientHooks.execute({ cloudPlatform: 'FIREBASE', token })
    }
    finally {
      this.subscribing = false
    }
  }

  private async unsubscribe(): Promise<void> {
    while (this.subscribing) await sleep(500)

    try {
      this.unsubscribing = true

      await this.messaging?.deleteToken()

      notificationClientHooks.execute(undefined)
    }
    finally {
      this.unsubscribing = false
    }
  }
}

export const baseFirebase = new BaseFirebase()